import type { ColumnDef, SortingState, VisibilityState } from '@tanstack/react-table'
import type { ReactNode } from 'react'

import { DataTable, type TablePagination } from '@/components/data/DataTable'
import { TableEmptyState } from '@/components/data/TableEmptyState'
import { cn } from '@/lib/cn'

/* The responsive list pattern, stated once so no surface reinvents it.
 *
 * THE RULE: below the table breakpoint, a row stops being a row. It becomes a
 * card carrying the two or three highest-priority fields; tapping opens the
 * record. Horizontally scrolling a grid is prohibited anywhere in the product —
 * a narrow screen is solved by CHANGING THE PATTERN, never by shrinking.
 *
 * Tablet keeps the table with fewer columns, chosen by the same priority order
 * the phone card uses. That is expressed per-surface with responsive column
 * classes rather than here, because only the surface knows its priority order.
 *
 * The desktop half is DataTable, so sorting, column visibility and the
 * virtualisation threshold are all DataTable's — this component only carries
 * the props through, which is why they are optional here for exactly the same
 * reason they are optional there.
 *
 * The phone card list is deliberately NOT windowed. Its cards are of variable
 * height, so windowing them needs post-paint measurement rather than the fixed
 * estimate the table gets, and the container it would need would be a nested
 * scroll region inside the page scroll — the one shape a phone handles worst,
 * and the thing the rule above exists to prevent. If a phone list ever grows
 * far enough to need it, `useWindowVirtualizer` against the page scroll is the
 * tool, not a second scrollable box.
 */

export interface RecordListProps<TData> {
  data: TData[]
  // Heterogeneous by construction — see the note in DataTable.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<TData, any>[]
  /** The phone form of one row. Gets the two or three fields that matter. */
  renderCard: (row: TData) => ReactNode
  getRowId: (row: TData) => string
  onRowClick?: (row: TData) => void
  /** Which rows `onRowClick` applies to — forwarded to DataTable, and on the
   *  phone a refused row is plain content rather than a `<button>` that does
   *  nothing (S-22f). Absent, every row opens. */
  isRowClickable?: (row: TData) => boolean
  empty?: ReactNode
  label: string
  density?: 'dense' | 'comfortable'
  /** Controlled sorting for the table half. Omitted, the table keeps its own. */
  sorting?: SortingState
  onSortingChange?: (next: SortingState) => void
  columnVisibility?: VisibilityState
  onColumnVisibilityChange?: (next: VisibilityState) => void
  /** Forwarded to DataTable, which owns the UX-DR-17 split between *nothing
   *  yet* and *nothing matches*. Forwarded rather than re-implemented for the
   *  reason the criterion exists: every surface that hand-builds the
   *  distinction words it differently, and two screens disagreeing about what
   *  an empty list means is the thing UX-DR-17 is about. The phone branch below
   *  honours the same split, so a narrow viewport does not lose the way back
   *  out of a filter — which is the viewport where a stuck filter is hardest to
   *  spot, because the facet controls are behind a menu. */
  filtered?: boolean
  onClearFilters?: () => void
  /** Pages the TABLE half only — see DataTable. The phone list is deliberately
   *  left whole: a card list is scrolled, not paged, and a pager at the bottom
   *  of a thumb-scroll is a button nobody reaches on purpose. */
  pagination?: TablePagination
}

export function RecordList<TData>({
  data,
  columns,
  renderCard,
  getRowId,
  onRowClick,
  isRowClickable,
  empty,
  label,
  density = 'dense',
  sorting,
  onSortingChange,
  columnVisibility,
  onColumnVisibilityChange,
  filtered = false,
  onClearFilters,
  pagination,
}: RecordListProps<TData>) {
  return (
    <>
      <div className="hidden md:block">
        <DataTable
          label={label}
          data={data}
          columns={columns}
          onRowClick={onRowClick}
          isRowClickable={isRowClickable}
          empty={empty}
          density={density}
          sorting={sorting}
          onSortingChange={onSortingChange}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={onColumnVisibilityChange}
          filtered={filtered}
          onClearFilters={onClearFilters}
          pagination={pagination}
        />
      </div>

      <div className="md:hidden">
        {data.length === 0 ? (
          /* The filtered branch WINS over a caller's own `empty` node, exactly
             as it does in DataTable: a bespoke "no invoices yet" shown over a
             filter that matched nothing sends the reader looking for records
             that are there. */
          filtered ? (
            <TableEmptyState filtered onClearFilters={onClearFilters} />
          ) : (
            empty
          )
        ) : (
          <ul className="flex flex-col">
            {data.map((row, index) => (
              <li key={getRowId(row)}>
                {onRowClick && isRowClickable && !isRowClickable(row) ? (
                  /* Plain content — the box of a card that opens, with no hover
                     and no pointer, because there is nothing to open. */
                  <div
                    className={cn(
                      'w-full px-4 py-3 text-left',
                      index !== data.length - 1 && 'border-line-hair border-b',
                    )}
                  >
                    {renderCard(row)}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    className={cn(
                      'w-full px-4 py-3 text-left',
                      'hover:bg-surface-hover transition-colors',
                      'duration-[var(--dur-instant)] ease-[var(--ease-standard)]',
                      index !== data.length - 1 && 'border-line-hair border-b',
                      onRowClick ? 'cursor-pointer' : 'cursor-default',
                    )}
                  >
                    {renderCard(row)}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  )
}

/** The standard card body: identifier and status on one line, the primary value
 *  under it, and the one figure that matters on the right. */
export function RecordCard({
  id,
  status,
  primary,
  secondary,
  value,
  valueLabel,
  flag,
}: {
  id: ReactNode
  status?: ReactNode
  primary: ReactNode
  secondary?: ReactNode
  value?: ReactNode
  valueLabel?: string
  flag?: ReactNode
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <span className="text-brand text-[13px] font-semibold">{id}</span>
        {status}
        <span className="flex-1" />
        {flag}
      </div>
      <div className="flex items-end justify-between gap-3">
        <span className="flex min-w-0 flex-col">
          <span className="text-text-heading truncate text-[13.5px] font-normal">
            {primary}
          </span>
          {secondary ? (
            <span className="text-text-muted truncate text-[12px] font-light">
              {secondary}
            </span>
          ) : null}
        </span>
        {value ? (
          <span className="flex shrink-0 flex-col items-end">
            {valueLabel ? (
              <span className="asa-eyebrow leading-none">{valueLabel}</span>
            ) : null}
            <span className="asa-num text-text-heading text-[13.5px]">{value}</span>
          </span>
        ) : null}
      </div>
    </div>
  )
}
